// «Рынок → Экспорт» и «Рынок → Опт»: удельная стоимость импорта по таможне
// (CIF; партнёр «Россия» — зеркало российского экспорта) и цена первой продажи
// за рубежом (партнёр «Россия» — российские суда), а «Опт России» — «Рыбные
// ряды» НФР в рублях, без столбца партнёра. Одна таблица, разные подписи.
// Групповая позиция (треска Gadus spp.) подписана группой: видового ряда из
// кода таможни не бывает.

import { useQuery } from '@tanstack/react-query'
import { cabinetApi } from '../api'
import { DASH, formatDay, formatMonth, formatNumber, useLang, useT } from '../i18n'
import { buildRows, cellTipKey, countryName, coverageLineKey, lastMonths, marketCurrency, marketKey, productKeys, yearsOf } from './customs'
import type { Market } from './customs'
import { usePricesUrl } from './pricesUrl'

const MONTHS_SHOWN = 6

export function CustomsPanel({ market = 'export' }: { market?: Market }) {
  const t = useT()
  const { lang } = useLang()
  const k = (key: string) => marketKey(market, key)
  const sp = useQuery({ queryKey: ['cabinet', 'prices', 'customs', market],
    queryFn: () => cabinetApi.customsSpecies(market), retry: false })
  const list = sp.data?.species ?? []
  const url = usePricesUrl()
  const urlKey = market === 'wholesale' ? 'wholesaleSpecies'
    : market === 'wholesale_ru' ? 'ruWholesaleSpecies' : 'exportSpecies'
  const cur = marketCurrency(market)
  const partners = market !== 'wholesale_ru'
  const picked = url.get(urlKey)
  const code = (list.some((s) => s.code === picked) ? picked : list[0]?.code) ?? null
  const setPicked = (c: string) => url.set({ [urlKey]: c })
  const series = useQuery({ queryKey: ['cabinet', 'prices', 'customs', market, code],
    queryFn: () => cabinetApi.customsSeries(code!, market), enabled: !!code, retry: false })

  if (sp.isLoading) return <p className="text-sm text-mist">{t('prices.loading')}</p>
  if (sp.error) return <p className="text-sm text-mist">{t('prices.error')}</p>
  if (!list.length) return <p className="text-sm text-foam">{t(k('empty'))}</p>

  const data = series.data
  const months = data ? lastMonths(data, MONTHS_SHOWN) : []
  const years = data ? yearsOf(data) : []
  const rows = data ? buildRows(data) : []
  const name = (s: (typeof list)[number]) => (lang === 'ru' ? s.name_ru : s.name_en)
  const current = list.find((s) => s.code === code)

  return (
    <div className="space-y-4 text-sm">
      <div className="flex flex-wrap gap-2">
        {list.map((s) => (
          <button key={s.code} type="button" onClick={() => setPicked(s.code)}
            className={`rounded-full border px-3 py-1 ${s.code === code
              ? 'border-accent text-foam' : 'border-line text-mist hover:text-foam'}`}>
            {name(s)}
          </button>
        ))}
      </div>
      {current?.covers && (
        <p className="text-xs text-mist">
          {t('prices.export.group', { species: current.covers.join(', ') })}
        </p>
      )}
      {series.isLoading && <p className="text-mist">{t('prices.loading')}</p>}
      {data && (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-xs">
            <thead>
              <tr className="text-left text-mist">
                <th className="py-1 pr-3 font-normal">{t(k('importer'))}</th>
                {partners && <th className="py-1 pr-3 font-normal">{t(k('from'))}</th>}
                <th className="py-1 pr-3 font-normal">{t('prices.export.product')}</th>
                {years.map((y) => <th key={y} className="py-1 pr-3 text-right font-normal">{y}</th>)}
                {months.map((m) => (
                  <th key={m} className="py-1 pr-3 text-right font-normal">{formatMonth(lang, m)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t border-line/50 text-foam">
                  <td className="py-1 pr-3">
                    {countryName(r.country, lang)}
                    {r.city && <span className="text-mist"> · {t(`prices.city.${r.city}`)}</span>}
                  </td>
                  {partners && (
                    <td className="py-1 pr-3">
                      {r.partner ? countryName(r.partner, lang) : t(k('allPartners'))}
                    </td>
                  )}
                  <td className="py-1 pr-3">
                    {[...productKeys(r).map((k) => t(k)), ...(r.size_grade ? [r.size_grade] : [])].join(', ')}
                  </td>
                  {years.map((y) => {
                    const c = r.years[y]
                    return (
                      <td key={y} className="py-1 pr-3 text-right tabular-nums"
                        title={c ? t(cellTipKey(c.annual ? 'yearAnnual' : 'year', c.tonnes), {
                          tonnes: formatNumber(lang, c.tonnes, 0), months: c.months ?? 0 })
                          : undefined}>
                        {c ? formatNumber(lang, c[cur], 2) : DASH}
                      </td>
                    )
                  })}
                  {months.map((m) => {
                    const c = r.months[m]
                    return (
                      <td key={m} className="py-1 pr-3 text-right tabular-nums"
                        title={c ? t(cellTipKey('month', c.tonnes), { tonnes: formatNumber(lang, c.tonnes, 0), rub: formatNumber(lang, c.rub, 0) }) : undefined}>
                        {c ? formatNumber(lang, c[cur], 2) : DASH}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-mist">{t(k('unit'))}</p>
        </div>
      )}
      {data && (
        <ul className="space-y-0.5 text-xs text-mist">
          {data.coverage.map((c) => (
            <li key={c.source}>
              {t(coverageLineKey(c.period), {
                // во вкладке «Опт России» обе строки — Россия, различает их источник
                country: partners ? countryName(c.country, lang) : t(`prices.wholesale_ru.source.${c.source}`),
                last: !c.last_month ? DASH
                  : c.period === 'year' ? c.last_month.slice(0, 4)
                    : c.period === 'week' || c.period === 'day' ? formatDay(lang, c.last_month)
                      : formatMonth(lang, c.last_month),
                published: formatDay(lang, c.published_at),
              })}
            </li>
          ))}
          <li>{t(k('coverageNote'))}</li>
        </ul>
      )}
    </div>
  )
}
